import { api } from "@/api/cliente"

/**
 * El personal al que se le puede asignar trabajo.
 *
 * ⚠ **No sale de `/usuarios`.** Esa ruta exige `usuario:administrar`, un permiso
 * que §8 le niega incluso al rol `supervisor` — que es justo quien coordina el
 * tablero. Con ella, el selector de asignado sólo habría funcionado para un
 * administrador y la tarjeta habría seguido diciendo «Usuario 3» a todos los demás.
 * `/usuarios/asignables` devuelve dos campos y pide `tarea:ver`.
 *
 * Se exporta la consulta y no un hook propio: `useQuery` se llama en la pantalla,
 * que es el único sitio donde hace falta, y el panel y el formulario reciben la
 * lista por props. Un hook envolvente aquí sólo añadiría una capa.
 */

export type Asignable = {
  id: number
  nombre: string
}

export const CONSULTA_ASIGNABLES = {
  queryKey: ["usuarios-asignables"],
  queryFn: () => api.get<Asignable[]>("/api/v1/usuarios/asignables"),
  // La plantilla de una agencia no cambia entre dos clics. Sin esto, cada filtro
  // del tablero volvería a pedir la lista entera.
  staleTime: 5 * 60_000,
}

/**
 * Devuelve el resolutor de nombres para una lista de asignables.
 *
 * Tres sitios lo necesitan —la tarjeta, el panel de detalle y la autoría de cada
 * comentario— y tenerlo una sola vez es lo que impide que uno de los tres se quede
 * enseñando el identificador crudo.
 *
 * El respaldo `Usuario N` no es decorativo: quien creó una tarea el año pasado puede
 * estar desactivado hoy, y esta lista sólo trae a los activos. Su identificador es
 * peor que su nombre, pero mucho mejor que un hueco.
 */
export function resolutorDeNombres(lista: Asignable[]): (id: number | null) => string | undefined {
  const porId = new Map(lista.map((u) => [u.id, u.nombre]))
  return (id) => (id === null ? undefined : (porId.get(id) ?? `Usuario ${id}`))
}
